import { useMutation, useQuery } from "@tanstack/react-query";
import { successMessage } from "../helpers/ErrorHandler";
import { ApiServiceErr, axiosApi } from "./apiService";

export interface Energy {
  id: string;
  Year: number;
  Percentage: number;
  Price: number;
  kWhConsumed: number;
  EnergyType: string;
}

export interface HeatSource {
  id: string;
  Year: number;
  Percentage: number;
  Price: number;
  Volumes: number;
  UnitType: string;
  HeatSource: string;
}

export interface RefrigerantSource {
  id: string;
  Year: number;
  Percentage: number;
  Price: number;
  Volumes: number;
  UnitType: string;
  RefrigerantSource: string;
}

export interface WaterSource {
  id: string;
  Year: number;
  Price: number;
  Volumes: number;
  WaterSource: string;
}

export interface Facility {
  _id?: string;
  facilityName: string;
  facilityAddress: string;
  facilityCity: any;
  facilityCountry: string;
  facilityOwnership: string;
  facilityType: string;
  status?: string;
  tenantId?: string;
  createdAt?: string;
  createdBy?: string;
  energy: Energy[];
  heatSource: HeatSource[];
  refrigerantSource: RefrigerantSource[];
  waterSource: WaterSource[];
  isSimulatedFacility?: boolean;
  originalId?: string | null;
  simulatedFacilityCount?: number;
}

export const useGetFacilityList = () =>
  useQuery(
    ["getFacilityWholeList"],
    async () => {
      const response = await axiosApi.get(`/facility/list`);
      return response.data;
    },
    {
      refetchOnWindowFocus: false
    }
  );

export const useGetFacilityPagesList = (
  pageNumber: number,
  pageLimit: number,
  searchKey: string,
  filteredFacilityTypes: string[]
) =>
  useQuery(
    ["GetFacilityPagesList", pageNumber, pageLimit, searchKey, filteredFacilityTypes],
    async () => {
      const response = await axiosApi.post(`/facility/list`, {
        pageNumber,
        pageSize: pageLimit,
        searchKey,
        filters: filteredFacilityTypes
      });
      return response.data;
    }
  );

export const useCreateFacility = (opt?: any) =>
  useMutation<Facility, ApiServiceErr, { data: Facility; simulatedFacility?: boolean }>(
    async ({
      data,
      simulatedFacility = false
    }: {
      data: Facility;
      simulatedFacility?: boolean;
    }) => {
      const response = await axiosApi.post(
        `/facility?simulatedFacility=${simulatedFacility}`,
        data
      );
      return response.data;
    },
    {
      onSuccess(data) {
        successMessage(
          data.isSimulatedFacility
            ? "Facility simulation created successfully"
            : "Facility created successfully"
        );
      },
      ...opt
    }
  );

export const useEditFacility = (opt?: any) =>
  useMutation<Facility, ApiServiceErr, { data: Facility; simulatedFacility?: boolean }>(
    async ({
      data,
      simulatedFacility = false
    }: {
      data: Facility;
      simulatedFacility?: boolean;
    }) => {
      const response = await axiosApi.put(
        `/facility/${data._id}?simulatedFacility=${simulatedFacility}`,
        data
      );
      return response.data;
    },
    {
      onSuccess(data) {
        successMessage(
          data.isSimulatedFacility
            ? "Facility simulation updated successfully"
            : "Facility updated successfully"
        );
      },
      ...opt
    }
  );

export const useGetFacilityById = (id: string) =>
  useQuery<Facility, ApiServiceErr>(
    [id, "getFacilityDataById"],
    async () => {
      const response = await axiosApi.get(`/facility/${id}`);
      return response?.data;
    },
    {
      cacheTime: 0,
      enabled: !!id
    }
  );

export const useDeleteFacility = () =>
  useMutation<Facility, ApiServiceErr, any>(async (data: Facility) => {
    data.status = "INACTIVE";
    const response = await axiosApi.put(`/facility/${data._id}`, data);
    return response.data;
  });

export const useGetFacilityHeatSources = (facilityId: string) =>
  useQuery<any, ApiServiceErr>(
    ["useGetFacilityHeatSources", facilityId],
    async () => {
      const response = await axiosApi.get(`/facility/${facilityId}/heat-sources`);
      const data = response?.data;
      return data.map((item: any) => ({
        key: item.id,
        text: item.heat_type,
        value: item.id
      }));
    },
    {
      cacheTime: 0,
      enabled: !!facilityId
    }
  );

export const useGetSimulatedFacilityPagesList = (
  originalId: string,
  pageNumber: number,
  pageLimit: number
) =>
  useQuery(
    ["GetSimulatedFacilityPagesList", originalId, pageNumber, pageLimit],
    async () => {
      const response = await axiosApi.get(
        `/facility/simulated-facilities/${originalId}?pageNumber=${pageNumber}&pageSize=${pageLimit}`
      );
      return response.data;
    },
    {
      enabled: !!originalId
    }
  );
